import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { StudioListPage, StudioProjectPage } from "./pages/studio-pages";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { AuthProvider, homeFor, useAuth } from "./auth";
import { AppLayout } from "./components/layouts";
import { Spinner } from "./components/ui";
import { applyTheme, readTheme } from "./theme";
import {
  ForgotPasswordPage,
  LegalPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
} from "./pages/auth-pages";
import { NotFoundPage } from "./pages/teacher-pages";
import { InvitePage, StudentHomePage } from "./pages/invite-page";
import { CourseTrailPage } from "./pages/learn/trail";
import { LessonPage } from "./pages/learn/lesson";
import {
  CertificateVerifyPage,
  FinalExamPage,
  FlashcardsPage,
  PracticePage,
  StudyToolsPage,
} from "./pages/learn/practice";
import { AccountPage } from "./pages/account-page";
import { PaymentStatusPage, PublicCoursePage, SimulatedCheckoutPage } from "./pages/public-pages";
import { applyFontSize } from "./components/extras";
import "./styles.css";

// Áreas do professor e do administrador em arquivos separados: o aluno baixa só o que usa.
const named = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const teacher = () => import("./pages/teacher-pages");
const EbookListPage = named(teacher, "EbookListPage");
const EbookUploadPage = named(teacher, "EbookUploadPage");
const EbookDetailPage = named(teacher, "EbookDetailPage");
const GenerationPage = named(() => import("./pages/generation-pages"), "GenerationPage");
const CoursesPage = named(() => import("./pages/course-workspace"), "CoursesPage");
const CourseWorkspace = named(() => import("./pages/course-workspace"), "CourseWorkspace");
const AdminPage = named(() => import("./pages/admin-pages"), "AdminPage");

applyTheme(readTheme());
applyFontSize();

// Aplicativo instalável: o service worker guarda a interface e as aulas já abertas para leitura sem internet.
if ("serviceWorker" in navigator && import.meta.env.PROD)
  window.addEventListener(
    "load",
    () => void navigator.serviceWorker.register("/sw.js").catch(() => undefined),
  );

function Home() {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="center">
        <Spinner />
      </div>
    );
  return <Navigate to={user ? homeFor(user) : "/entrar"} replace />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <Suspense
          fallback={
            <div className="center">
              <Spinner />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/entrar" element={<LoginPage />} />
            <Route path="/cadastro" element={<RegisterPage />} />
            <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
            <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
            <Route path="/documentos" element={<LegalPage />} />
            <Route path="/convite/:token" element={<InvitePage />} />
            <Route path="/certificado" element={<CertificateVerifyPage />} />
            <Route path="/certificado/:code" element={<CertificateVerifyPage />} />
            <Route path="/curso/:courseId" element={<PublicCoursePage />} />
            <Route element={<AppLayout adminOnly />}>
              <Route path="/admin" element={<AdminPage />} />
            </Route>
            <Route element={<AppLayout teacherOnly />}>
              <Route path="/professor/ebooks" element={<EbookListPage />} />
              <Route path="/professor/ebooks/novo" element={<EbookUploadPage />} />
              <Route path="/professor/ebooks/:id" element={<EbookDetailPage />} />
              <Route path="/professor/estudio" element={<StudioListPage />} />
              <Route path="/professor/estudio/:id" element={<StudioProjectPage />} />
              <Route path="/professor/cursos" element={<CoursesPage />} />
              <Route path="/professor/cursos/:id" element={<CourseWorkspace />} />
              <Route path="/professor/cursos/:id/geracao" element={<GenerationPage />} />
            </Route>
            <Route element={<AppLayout />}>
              <Route path="/aluno" element={<StudentHomePage />} />
              <Route path="/conta" element={<AccountPage />} />
              <Route path="/pagamento/:paymentId" element={<PaymentStatusPage />} />
              <Route path="/pagamento/simulado/:paymentId" element={<SimulatedCheckoutPage />} />
              <Route path="/aluno/cursos/:courseId" element={<CourseTrailPage />} />
              <Route path="/aluno/cursos/:courseId/aulas/:lessonId" element={<LessonPage />} />
              <Route path="/aluno/cursos/:courseId/revisao" element={<FlashcardsPage />} />
              <Route path="/aluno/cursos/:courseId/modulos/:moduleId" element={<PracticePage />} />
              <Route path="/aluno/cursos/:courseId/avaliacao" element={<FinalExamPage />} />
              <Route path="/aluno/cursos/:courseId/consulta" element={<StudyToolsPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  </StrictMode>,
);
